import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';

import { usePostScanSession } from 'services/face-scan/hooks';
import { InititaQRSession } from 'services/face-scan/type';
import { SessionError, USER_ACTIVITY, WSScanStatus } from '../../types';
import { captureUserActivity } from 'services/profile/api';
import Scanning from './Scanning';
import StartScan from './StartScan';
import { PATH_ROUTES } from 'routes/path';
import ScanError from './ScanError';
import ScanStopped from './ScanStopped';
import ScanWithMobile from './ScanWithMobile';
import ScanSessionExpired from './ScanSessionExpired';
import ScanInProgress from './ScanInProgress';
import useSocket from 'hooks/useSocket';
import Loader from 'components/Loader';

interface ScanPreferenceProps {
  onWebSelect: () => void;
}

export default function ScanPreference({ onWebSelect }: ScanPreferenceProps) {
  const navigate = useNavigate();

  const [isGeneratingQR, setIsGeneratingQR] = useState<boolean>(false);
  const [qrData, setQrData] = useState<InititaQRSession>({
    url: '',
    session_id: '',
    device_token_flag: true,
  });
  const [selectedDevice, setSelectedDevice] = useState<'app' | 'web'>();
  const [sessionError, setSessionError] = useState<SessionError>();
  const [isSessionOngoing, setIsSessionOngoing] = useState<boolean>(false);
  const [isInitiating, setIsInitiating] = useState<boolean>(false);
  const [isAppScan, setIsAppScan] = useState<boolean>(false);

  const { connectWebSocket, socket, wsStatusData, setWsStatusData } =
    useSocket();

  const { mutate, mutateAsync, isPending } = usePostScanSession({
    onSuccess: (data, variables) => {
      if ((variables as { status: string }).status === 'initiate_qr') {
        setQrData(data as InititaQRSession);
      }
      if ((variables as { status: string }).status === 'ongoing_session') {
        setIsSessionOngoing(false);
      }
    },
    onError: (err) => {
      if (err instanceof AxiosError && err?.response) {
        const res = err.response.data;
        setSessionError(res);
        setIsSessionOngoing(true);
      }
      setQrData({ url: '', session_id: '', device_token_flag: true });
    },
    onSettled: () => setIsInitiating(false),
  });

  const onInitiateSession = () => {
    mutate({ status: 'ongoing_session' });
  };

  useEffect(() => {
    if (!isInitiating) setIsInitiating(true);
    onInitiateSession();
  }, []);

  const onGenerate = async () => {
    setIsGeneratingQR(true);

    let initiateQrRes;
    try {
      initiateQrRes = await mutateAsync({ status: 'initiate_qr' });
    } catch (error) {
      console.error(error);
      setIsGeneratingQR(false);
      return;
    }

    try {
      await captureUserActivity(USER_ACTIVITY.GENERATE_QR);
    } catch (error) {
      console.error(error);
    }

    connectWebSocket({
      sessionId: initiateQrRes.session_id,
      onConnect: () => {
        setSelectedDevice('app');
        setIsGeneratingQR(false);
      },
    });

    return initiateQrRes.url;
  };

  const onDialogClose = () => {
    setWsStatusData(null);
    setQrData({ url: '', session_id: '', device_token_flag: true });
    if (socket) socket.close();
    navigate(PATH_ROUTES.FACE_SCAN);
  };

  useEffect(() => {
    if (wsStatusData?.status === WSScanStatus.END_SCAN) {
      navigate(PATH_ROUTES.FACE_SCAN_RESULT);
    }
  }, [wsStatusData?.status]);

  const onBack = () => {
    if (socket) socket.close();
    setSelectedDevice(undefined);
  };

  if (isInitiating) {
    return <Loader className='h-[79vh]' />;
  }

  if (!qrData.url && isSessionOngoing) {
    return (
      <ScanInProgress error={sessionError} onInitiate={onInitiateSession} />
    );
  }

  if (!selectedDevice) {
    return (
      <StartScan
        isLoading={isPending}
        isGeneratingQR={isGeneratingQR}
        onGenerate={onGenerate}
        onWebSelect={onWebSelect}
        setIsAppScan={setIsAppScan}
      />
    );
  }

  if (wsStatusData?.status === WSScanStatus.START_SCAN) {
    return <Scanning onClose={onDialogClose} />;
  }

  if (wsStatusData?.status === WSScanStatus.SCAN_ERROR) {
    return (
      <ScanError errors={wsStatusData?.error || []} onClose={onDialogClose} />
    );
  }

  if (wsStatusData?.status === WSScanStatus.STOP_SCAN) {
    return <ScanStopped onClose={onDialogClose} />;
  }

  if (wsStatusData?.status === WSScanStatus.SESSION_EXPIRED) {
    return <ScanSessionExpired onClose={onDialogClose} />;
  }

  if (isAppScan && qrData?.url) {
    return (
      <StartScan
        isLoading={isPending}
        isGeneratingQR={isGeneratingQR}
        onGenerate={onGenerate}
        onWebSelect={onWebSelect}
      />
    );
  }

  const regenerate = async () => {
    if (socket) socket.close();
    await onGenerate();
  };

  return (
    <ScanWithMobile
      qrData={qrData}
      onBack={onBack}
      onGenerate={regenerate}
      isLoading={isGeneratingQR}
      connectWebSocket={connectWebSocket}
    />
  );
}
